import React from "react";
import { Typography, Box, Chip, Stack } from "@mui/material";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import BlockIcon from "@mui/icons-material/Block";
import Tooltip from "@mui/material/Tooltip";

import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";


const getColorStyles = (status) => {
  switch (status) {
    case "1": // Completed – Green
      return {
        border: "#22C55E",
        bg: "#22C55E0D",
        label: "Completed",
      };

    case "2": // N/A – Light Grey (SC)
      return {
        border: "#9CA3AF",
        bg: "#9CA3AF0D",
        label: "N/A",
      };

    case "3": // Unsuccessful – Red (SC)
      return {
        border: "#DC2626",
        bg: "#DC26260D",
        label: "Unsuccessful",
      };

    case "4": // Not Started – Grey
      return {
        border: "#6B7280",
        bg: "#6B72800D",
        label: "Not Started",
      };

    case "5": // On Hold – Orange / Amber
      return {
        border: "#F59E0B",
        bg: "#F59E0B0D",
        label: "On Hold",
      };

    case "0": // In Process – Blue
      return {
        border: "#006CB8",
        bg: "#EBF2FF",
        label: "In Process",
      };
    default:
      return {
        border: "#9CA3AF",
        bg: "#9CA3AF0D",
        label: "N/A",
      };
  }
};

const getStageColor = (status) => {
  switch (status) {
    case "1": // Completed
      return {
        border: "#22C55E",
        bg: "#22C55E0D",
        label: "Completed",
      };

    case "2": // N/A – Light Grey (SC)
      return {
        border: "#9CA3AF",
        bg: "#9CA3AF0D",
        label: "N/A",
      };

    case "3": // Unsuccessful – Red (SC)
      return {
        border: "#DC2626",
        bg: "#DC26260D",
        label: "Unsuccessful",
      };

    case "4": // Not Started – Grey
      return {
        border: "#6B7280",
        bg: "#6B72800D",
        label: "Not Started",
      };

    case "5": // On Hold – Orange / Amber
      return {
        border: "#F59E0B",
        bg: "#F59E0B0D",
        label: "On Hold",
      };

    case "0": // In Process – Blue
    default:
      return {
        border: "#006CB8",
        bg: "#006CB80D",
        label: "In Process",
      };
  }
};



const getIcon = (status, color) => {
  const iconProps = { sx: { color, fontSize: 34 } };

  switch (status) {
    case "Completed":
      return (
        <Tooltip title="Completed" arrow>
          <CheckCircleOutlineIcon {...iconProps} />
        </Tooltip>
      );

    case "In Process":
      return (
        <Tooltip title="In Process" arrow>
          <AccessTimeIcon {...iconProps} />
        </Tooltip>
      );

    case "Not Started":
      return (
        <Tooltip title="Not Started" arrow>
          <PlayCircleOutlineIcon {...iconProps} />
        </Tooltip>
      );

    case "On Hold":
      return (
        <Tooltip title="On Hold" arrow>
          <PauseCircleOutlineIcon {...iconProps} />
        </Tooltip>
      );

    case "Unsuccessful":
      return (
        <Tooltip title="Unsuccessful" arrow>
          <HighlightOffIcon {...iconProps} />
        </Tooltip>
      );

    case "N/A":
      return (
        <Tooltip title="Not Applicable" arrow>
          <BlockIcon {...iconProps} />
        </Tooltip>
      );

    default:
      return null;
  }
};


function getExpiryStatus(field) {
  if (field.RPF_Type !== "Date") return null;

  // 🔹 Check if expiry is enabled
  if (field.RPF_IsExpiry == undefined) {
    return null;
  }

  // 1. Parse expiry period
  let expiryDays = 0;
  if (field.RPF_ExpiryPeriod && field.RPF_IsExpiry != undefined) {
    try {
      expiryDays = Number(field.RPF_ExpiryPeriod.toString().replace(/"/g, ""));
    } catch {
      expiryDays = 0;
    }
  }

  // 2. Parse the base date (RPF_Value)
  if (!field.RPF_Value) return null;
  const baseDate = new Date(field.RPF_Value);
  if (isNaN(baseDate)) return null;

  // 3. Calculate expiry date
  const expiryDate = new Date(baseDate);
  expiryDate.setDate(baseDate.getDate() + expiryDays);

  // 4. Compare with today
  const today = new Date();
  const diffTime = expiryDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Helper for singular/plural
  const dayLabel = (n) => (Math.abs(n) === 1 ? "day" : "days");

  // 5. Handle result
  if (diffDays > 30) {
    return null; // don’t show if more than 30 days left
  } else if (diffDays > 0) {
    return {
      value: `${field.RPF_Name}: ${diffDays} ${dayLabel(diffDays)} pending`,
      fontSize: "16px",
      color: "black",
    };
  } else if (diffDays === 0) {
    return {
      value: `${field.RPF_Name}: Expires today`,
      fontSize: "16px",
      color: "black",
    };
  } else if (diffDays >= -60) {
    return {
      value: `${field.RPF_Name}: Expired ${Math.abs(diffDays)} ${dayLabel(
        diffDays
      )} ago`,
      fontSize: "16px",
      color: "red",
    };
  } else {
    return null; // don’t show if expired more than 60 days ago
  }
}

function HorizontalStageBox({ stages = [] }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        overflowX: "auto",
      }}
    >
      {stages?.map((stage, stageIndex) => {
        const hasSubProcedures =
          stage?.subProcedures && stage.subProcedures.length > 0;

        let stageStatus = 4;

        const subComponents = hasSubProcedures
          ? stage.subProcedures.map((sub) => {
            const fields = sub?.fields || [];
            const statusField = fields.find(
              (field) => field?.RPF_Type === "IsCompleted"
            );
            const status = [statusField?.RPF_Value].flat()[0] || "4";
            return { ...sub, isCompleted: status };
          })
          : [];

        if (!hasSubProcedures) {
          const stageField = stage?.fields?.find(
            (field) => field?.RPF_Type === "IsCompleted"
          );
          stageStatus = [stageField?.RPF_Value].flat()[0] || "4";
        } else {
          const statuses = subComponents.map((s) => s.isCompleted);

          if (statuses.every((s) => s == 1)) {
            // All completed
            stageStatus = "1";
          } else if (statuses.every((s) => s == 2)) {
            // All N/A
            stageStatus = "2";
          } else if (statuses.some((s) => s == 3)) {
            // Any unsuccessful
            stageStatus = "3";
          } else if (statuses.some((s) => s == 5)) {
            // Any on hold
            stageStatus = "5";
          } else if (statuses.every((s) => s == 4)) {
            // All not started
            stageStatus = "4";
          } else {
            // Mixed or in progress
            stageStatus = "";
          }
        }

        const { border: stageBorderColor, bg: stageBgColor } = getColorStyles(
          String(stageStatus)
        );
        const fieldsOnly = stage?.fields || [];

        const stageStatusFieldsMain = fieldsOnly.filter((field) =>
          field?.RPF_Name?.trim()?.toLowerCase().includes("status")
        );

        const allStageStatusFields = [...stageStatusFieldsMain];

        return (
          <Box
            key={stageIndex}
            sx={{
              minWidth: "250px",
              border: stageStatus
                ? `2px solid ${stageBorderColor}`
                : "2px solid lightgray",
              backgroundColor: stageBgColor,
              borderRadius: 3,
              p: 2,
              m: 1,
              mt: 3,
              boxShadow: "0px 4px 10px rgba(0,0,0,0.06)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
            }}
          >
            {/* Header Status Icon*/}
            <Box
              sx={{
                position: "relative",
                bottom: `25px`,
                left: "-25px",
                borderRadius: "50%",
                backgroundColor: stageStatus ? "#fff" : "transparent",
                width: "30px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {stageStatus &&
                getIcon(
                  stageStatus == 1
                    ? "Completed"
                    : stageStatus == 2
                      ? "N/A"
                      : stageStatus == 3
                        ? "Unsuccessful"
                        : stageStatus == 4
                          ? "Not Started"
                          : stageStatus == 5
                            ? "On Hold"
                            : "In Process",
                  stageBorderColor
                )}
            </Box>

            {/* Header Status Text*/}
            <Chip
              label={
                stageStatus == 1
                  ? "Completed"
                  : stageStatus == 2
                    ? "N/A"
                    : stageStatus == 3
                      ? "Unsuccessful"
                      : stageStatus == 4
                        ? "Not Started"
                        : stageStatus == 5
                          ? "On Hold"
                          : "In Process"
              }
              sx={{
                alignSelf: "flex-start",
                fontSize: 16,
                fontWeight: 600,
                height: 22,
                backgroundColor: stageStatus ? stageBorderColor : "transparent",
                color: stageStatus ? "#fff" : "transparent",
                position: "relative",
                bottom: `58px`,
                left: "20px",
              }}
            />

            {/* Procedure Title Text */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 1.2,
                // border:'2px solid red',
                position: "relative",
                bottom: "40px",
                maxHeight: "50px",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                <Typography
                  fontWeight={600}
                  fontSize={16}
                  sx={{
                    fontSize: "18px",
                    textTransform: "capitalize",
                    fontFamily: "Geist, sans-serif",
                  }}
                >
                  {stage?.RPS_Name || "Unnamed Stage"}
                </Typography>

                {stage?.fields?.map((field) => {
                  const status = getExpiryStatus(field);
                  return (
                    <Box key={field.RPF_Id}>
                      {status?.value && (
                        <Typography
                          sx={{
                            fontSize: status?.fontSize,
                            color: status?.color,
                            textTransform: "Capitalize",
                            textAlign: "left",
                          }}
                        >
                          {status?.value}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Procedure Status Text */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: 1,
                // border:'2px solid red',
                position: "relative",
                // bottom: "40px",
              }}
            >
              {allStageStatusFields
                .filter((field) => field.RPF_Value) // only keep fields with a value
                .map((field, index) => (
                  <Typography
                    key={index}
                    fontWeight={600}
                    sx={{
                      textTransform: "capitalize",
                      fontFamily: "Geist, sans-serif",
                      fontSize: "14px",
                      // color: stageBorderColor,
                      mb: 0.5, // spacing between lines
                    }}
                  >
                    {field.RPF_Name} : {field.RPF_Value || "---"}
                  </Typography>
                ))}
            </Box>

            {/* Sub Procedure  */}
            {hasSubProcedures &&
              subComponents.map((sub, subIndex) => {
                const statusKey = String(sub.isCompleted ?? "4");
                const statusMap = getColorStyles(statusKey);


                // ✅ Status fields ONLY for this sub-procedure
                const subStatusFields = (sub?.fields || [])
                  .filter((field) =>
                    field?.RPF_Name?.trim()?.toLowerCase().includes("status")
                  )
                  .filter((field) => field?.RPF_Value); // only non-empty values

                return (
                  <Box
                    key={subIndex}
                    sx={{
                      borderLeft: `4px solid ${statusMap.border}`,
                      backgroundColor: statusMap.bg,
                      p: 1,
                      my: 1,
                      borderRadius: 1,
                      //    border:'2px solid red',
                      position: "relative",
                      bottom: "40px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          bottom: `0px`,
                          left: "-25px",
                          borderRadius: "50%",
                          backgroundColor: "#fff",
                          width: "30px",
                          height: "30px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {getIcon(statusMap.label, statusMap.border)}
                      </Box>

                      <Typography
                        sx={{
                          fontFamily: "Geist, sans-serif",
                          fontSize: 14,
                          fontWeight: 500,
                          textTransform: "capitalize",
                          position: "relative",
                          left: "-25px",
                        }}
                      >
                        {sub?.RSP_Name || "Unnamed Sub"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        alignContent: "center",
                        justifyContent: "center",
                      }}
                    >
                      {subStatusFields.map((field, index) => (
                        <Typography
                          key={index}
                          fontWeight={600}
                          sx={{
                            textTransform: "capitalize",
                            fontFamily: "Geist, sans-serif",
                            fontSize: "14px",
                            // color: statusMap.border,
                            mb: 0.5,
                          }}
                        >
                          {field.RPF_Name} : {field.RPF_Value || "---"}
                        </Typography>
                      ))}

                      {/* New Implementation - 09-09-25 - Expiry Date */}
                      {sub?.fields?.map((field) => {
                        const status = getExpiryStatus(field);
                        return (
                          <Box key={field.RPF_Id}>
                            {status && (
                              <Typography
                                sx={{
                                  fontSize: status?.fontSize,
                                  color: status?.color,
                                  textTransform: "Capitalize",
                                }}
                              >
                                {status?.value}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}

                      {sub?.fields?.map((field) => {
                        const isAttachment = field?.RPF_Type === "Attachment";

                        // -----------------------------
                        // ATTACHMENT LOGIC
                        // -----------------------------
                        if (
                          isAttachment &&
                          field?.RPF_Value &&
                          field?.RPF_Value !== "" &&
                          field?.RPF_Value?.attachment

                        ) {
                          const fileName = field?.RPF_Value?.fileName || "";
                          const attachment = field?.RPF_Value?.attachment;
                          const fileUrl = `${process.env.REACT_APP_BASE_URL}/${attachment}`;
                          const extension = fileName.split(".").pop()?.toLowerCase();

                          const getIcon = () => {
                            if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension)) {
                              return <ImageIcon />;
                            }
                            if (extension === "pdf") {
                              return <PictureAsPdfIcon color="error" />;
                            }
                            if (["doc", "docx", "txt", "rtf"].includes(extension)) {
                              return <DescriptionIcon color="primary" />;
                            }
                            return <InsertDriveFileIcon />;
                          };

                          const handleClick = () => {
                            window.open(fileUrl, "_blank", "noopener,noreferrer");
                          };
                          if(!field?.RPF_IsViewable){
                            return
                          }
                          return (
                            <Box
                              key={field.RPF_Id}
                              onClick={handleClick}
                              sx={{
                                position: "relative",
                                border: "1px solid",
                                borderColor: "grey.400",
                                borderRadius: 2,
                                p: 2,
                                pt: 3,
                                my: 2,
                                cursor: "pointer",
                                transition: "0.2s",
                                wordBreak: "break-word",
                                "&:hover": {
                                  backgroundColor: "grey.100",
                                },
                              }}
                            >
                              {/* Border Label */}
                              <Typography
                                variant="caption"
                                sx={{
                                  position: "absolute",
                                  top: -10,
                                  left: 16,
                                  px: 1,
                                  backgroundColor: 'white',
                                  border: `2px solid ${statusMap.border}`,
                                  fontWeight: 500,
                                  borderRadius:2
                                }}
                              >
                                {field?.RPF_Name}
                              </Typography>

                              <Stack direction="row" spacing={2} alignItems="center">
                                {getIcon()}
                                <Typography variant="body2">
                                  {fileName}
                                </Typography>
                              </Stack>
                            </Box>
                          );
                        }
                      })}

                    </Box>
                  </Box>
                );
              })}
            <Box>

              {stage?.fields?.map((field) => {
                const isAttachment = field?.RPF_Type === "Attachment";

                // 🚫 Skip if not attachment
                if (!isAttachment) return null;

                // // 🚫 Skip if RPF_Value is empty string, null, undefined
                if (
                  !field?.RPF_Value ||
                  field?.RPF_Value === "" ||
                  !field?.RPF_Value?.attachment
                ) {
                  return null;
                }

                const fileName = field?.RPF_Value?.fileName || "";
                const attachment = field?.RPF_Value?.attachment;
                const fileUrl = `${process.env.REACT_APP_BASE_URL}/${attachment}`;

                const extension = fileName.split(".").pop()?.toLowerCase();

                const getIcon = () => {
                  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension)) {
                    return <ImageIcon />;
                  }
                  if (extension === "pdf") {
                    return <PictureAsPdfIcon color="error" />;
                  }
                  if (["doc", "docx", "txt", "rtf"].includes(extension)) {
                    return <DescriptionIcon color="primary" />;
                  }
                  return <InsertDriveFileIcon />;
                };



                const handleClick = () => {
                  window.open(fileUrl, "_blank", "noopener,noreferrer");
                };
                if(!field?.RPF_IsViewable){
                  return
                }
                return (
                  <Box
                    key={field.RPF_Id}
                    onClick={handleClick}
                    sx={{
                      position: "relative",
                      border: "1px solid",
                      borderColor: "grey.400",
                      borderRadius: 2,
                      p: 2,
                      pt: 3,
                      mb: 2,
                      cursor: "pointer",
                      transition: "0.2s",
                      wordBreak: "break-word",
                      "&:hover": {
                        backgroundColor: "grey.100",
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        top: -10,
                        left: 16,
                        px: 1,
                        border: `2px solid ${stageBorderColor}`, backgroundColor: 'white',
                        fontWeight: 500,
                        borderRadius:2
                      }}
                    >
                      {field?.RPF_Name} 
                    </Typography>

                    <Stack direction="row" spacing={2} alignItems="center">
                      {getIcon()}
                      <Typography variant="body2">
                        {fileName}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })}


            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default HorizontalStageBox;
